import React from "react";
import { useSite } from "../context/site";
import { PageHero, CtaBand, TrajectoryBand } from "../components/site/kit";
import { NextSteps, Pager } from "../components/site/extras";
import { Reveal } from "../components/site/motion";
import { MODEL_STEPS, CAPABILITIES, DISCIPLINES } from "../lib/content";

const Spine = () => (
  <div className="relative h-full min-h-[420px] w-full" aria-hidden>
    <div className="absolute bottom-3 left-1/2 top-2 w-[3px] -translate-x-1/2 bg-[#5f8f10]" />
    <div className="absolute bottom-0 left-1/2 -translate-x-1/2" style={{ borderLeft: "7px solid transparent", borderRight: "7px solid transparent", borderTop: "12px solid #5f8f10" }} />
    {[10, 37, 63, 90].map((top, i) => (
      <div key={i} className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2" style={{ top: `${top}%` }}>
        <span className="block h-3.5 w-3.5 bg-[#5f8f10]" />
        <span className="absolute -inset-1.5 border border-[#5f8f10]/35" />
      </div>
    ))}
  </div>
);

export default function HowWeWork() {
  const { openDiagnostic } = useSite();
  return (
    <div className="font-body bg-white">
      <PageHero
        testId="howwework-hero"
        eyebrow="How we work"
        crumbs={[{ label: "Home", href: "/" }, { label: "How We Work" }]}
        title={<>Strategy built<br />to be <span className="text-[#5f8f10]">executed.</span></>}
        subcopy="The problem is not the absence of strategy. It is that most strategies are built apart from the realities of execution. We bring the two together from the first move."
      />

      {/* The SWITCH model, four moves */}
      <section className="border-b border-white/10 bg-[#161A15]">
        <div className="mx-auto max-w-[1360px] px-6 py-24 md:px-12 md:py-32 lg:px-16">
          <div className="max-w-3xl">
            <p className="font-body text-sm font-semibold uppercase tracking-[0.18em] text-white/60">The SWITCH model</p>
            <h2 className="font-display mt-6 text-4xl leading-[0.95] tracking-tight text-white md:text-5xl lg:text-6xl">Four moves, one agenda.</h2>
          </div>
          <div className="mt-16 grid gap-10 lg:grid-cols-12">
            <div className="hidden lg:col-span-1 lg:block">
              <Spine />
            </div>
            <div className="lg:col-span-11">
              {MODEL_STEPS.map((st, i) => (
                <Reveal key={st.n} delay={i * 0.12} className={`group grid items-baseline gap-6 py-10 md:grid-cols-12 md:gap-10 ${i !== 0 ? "border-t border-white/12" : ""}`}>
                  <div className="md:col-span-3">
                    <span className="font-display block text-6xl leading-none text-white/25 transition-colors duration-300 group-hover:text-[#5f8f10] md:text-7xl">{st.n}</span>
                  </div>
                  <div className="md:col-span-9">
                    <h3 className="font-display text-2xl uppercase leading-[1.02] tracking-tight text-[#5f8f10] md:text-3xl">{st.title}</h3>
                    <p className="font-body mt-4 max-w-2xl text-lg font-normal leading-relaxed text-white/70">{st.desc}</p>
                  </div>
                </Reveal>
              ))}
              <p className="font-body mt-10 border-t border-white/12 pt-8 text-lg font-medium italic text-white/60">
                The problem determines the expertise, not the other way around.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Collective expertise */}
      <section className="bg-[#F7F6F2]">
        <div className="mx-auto max-w-[1360px] px-6 py-24 md:px-12 md:py-32 lg:px-16">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5 lg:sticky lg:top-28 lg:self-start">
              <p className="font-body text-sm font-semibold uppercase tracking-[0.18em] text-black/60">Collective expertise</p>
              <h2 className="font-display mt-6 text-4xl leading-[0.95] tracking-tight text-black md:text-5xl lg:text-6xl">
                Every discipline that moves growth, <span className="text-[#5f8f10]">in one room.</span>
              </h2>
              <p className="font-body mt-6 max-w-md text-lg font-normal leading-relaxed text-black/80">
                We do not specialise in one lever. The problem decides which disciplines come together around a single growth agenda.
              </p>
              <div className="mt-8 flex flex-wrap gap-2">
                {DISCIPLINES.map((d) => (
                  <span key={d} className="font-body border border-black/20 px-4 py-2 text-sm font-semibold uppercase tracking-[0.1em] text-black/70">{d}</span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="border-t border-black/15">
                {CAPABILITIES.map((c, i) => (
                  <Reveal key={c.title} delay={(i % 4) * 0.08} className="group border-b border-black/15 py-6">
                    <div className="flex items-baseline gap-6">
                      <span className="font-display w-14 shrink-0 text-4xl leading-none text-[#5f8f10] md:text-5xl">0{i + 1}</span>
                      <h3 className="font-display text-xl uppercase leading-[1.05] tracking-tight text-black transition-colors duration-200 group-hover:text-[#5f8f10] md:text-2xl">{c.title}</h3>
                    </div>
                    <p className="font-body mt-2 pl-20 text-base font-normal leading-relaxed text-black/60">{c.desc}</p>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <TrajectoryBand src="/assets/editorial-execute.jpg" testId="howwework-band" label="From strategy to execution" />

      <NextSteps links={[
        { eyebrow: "What we solve", title: "Four ways growth gets stuck", blurb: "See the situations this model is built to resolve.", href: "/what-we-solve" },
        { eyebrow: "Our work", title: "Proof, not promises", blurb: "The model, applied to real growth problems.", href: "/our-work" },
        { eyebrow: "About", title: "Why we exist", blurb: "A growth company built to turn strategy into execution.", href: "/about" },
      ]} />

      <Pager current="/how-we-work" />

      <CtaBand
        heading={<>Tell us what is not<br />moving in your business.</>}
        onClick={() => openDiagnostic()}
        testId="how-diagnostic-cta"
      />
    </div>
  );
}
